import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { movies } from "@/mocks/movies";
import Breadcrumb from "@/components/feature/Breadcrumb";
import DetailHero from "@/pages/detail/components/DetailHero";
import ReviewSection from "@/pages/detail/components/ReviewSection";
import RelatedMovies from "@/pages/detail/components/RelatedMovies";

export default function Detail() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id.toString() === id);

  useSeo({
    title: movie
      ? `${movie.title}高清电影详情与剧情介绍 | 《出轨的妻子》hd`
      : "影片详情 | 《出轨的妻子》hd",
    description: movie
      ? `${movie.title}（${movie.year}·${movie.region}）高清电影详情：导演${movie.director}，主演${movie.cast.join("、")}。${movie.synopsis.slice(0, 60)}…`
      : "《出轨的妻子》hd 影片详情与剧情介绍页面。",
    keywords: movie
      ? `${movie.title},${movie.title}剧情,${movie.title}高清,${movie.genres.join(",")},高清影视资源`
      : "出轨的妻子,高清电影,剧情介绍",
  });

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-3xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该影片</h1>
        <p className="mt-2 max-w-md text-sm text-foreground-600">
          该影片可能已下线或链接有误，请返回分类页浏览其他高清影视资源。
        </p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          返回电影分类
        </Link>
      </div>
    );
  }

  return (
    <>
      <DetailHero movie={movie} />

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "电影分类", to: "/category" },
            { label: movie.title },
          ]}
        />

        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0">
            <section aria-label="剧情介绍">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                剧情介绍
              </h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">{movie.synopsis}</p>
            </section>

            <ReviewSection movie={movie} />
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950">影片信息</h3>
              <dl className="mt-4 space-y-3 font-label text-sm">
                {[
                  { k: "片名", v: movie.title },
                  { k: "上映年份", v: `${movie.year} 年` },
                  { k: "地区", v: movie.region },
                  { k: "类型", v: movie.genres.join(" / ") },
                  { k: "导演", v: movie.director },
                  { k: "主演", v: movie.cast.join("、") },
                  { k: "片长", v: movie.duration },
                  { k: "评分", v: `${movie.score.toFixed(1)} / 10` },
                ].map((row) => (
                  <div key={row.k} className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-500">{row.k}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{row.v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg bg-secondary-50 p-4 md:p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-lightbulb-line text-lg text-accent-500" aria-hidden="true"></i>
                观影小贴士
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                建议在安静环境下观看本片，留意反复出现的雨夜与信件意象，它们往往暗示着角色内心未说出口的情绪，也是理解剧情反转的关键线索。
              </p>
            </div>
          </aside>
        </div>

        <RelatedMovies current={movie} />
      </div>
    </>
  );
}